/**
 * `createNetworksSource` — the caller's private networks, over the shared `ApiClient`.
 *
 * Speaks canonical `/metadata-networks` paths; `RESOURCE_REWRITES` sends them to
 * metadata-registry-api's `/v0/networks`, so region routing, auth and envelope
 * unwrap are inherited.
 *
 * **This adapter never names a tenancy.** The backend reads the caller's own
 * rungs and nothing else: a sibling user's local network is a 404, not a row
 * to filter out. What comes back names its owner by RUNG only (`user` — yours;
 * `tenant` — your tenant's), which is all the page shows.
 *
 * Every write also changes the chain registry the pickers read (a network is a
 * chain only its owners see), so the page refreshes the effective chains after
 * one — see `refreshEffectiveChains`.
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";
import { PrivateNetworkViewSchema, type PrivateNetworkViewDTO } from "@authe/fe-commons/models";
import { asRecord, rowsFromWire } from "./wire";

/** The path every call here rewrites from. */
const PATH = "/metadata-networks";

export type PrivateNetwork = PrivateNetworkViewDTO;

export interface CreateNetworkInput {
  /** An EVM network is named by its chain id (`31337` → `eip155:31337`). */
  chainId?: number;
  caip2?: string;
  family?: "evm" | "corda" | "fabric" | "canton";
  participantId?: string;
  name: string;
  /** `local` — yours alone; `private` — your tenant's. */
  networkKind: "local" | "private";
  ownerRung?: PrivateNetwork["owner_rung"];
  nativeToken?: string;
}

export interface NetworksSource {
  list(): Promise<PrivateNetwork[]>;
  create(input: CreateNetworkInput): Promise<PrivateNetwork>;
  setLogo(caip2: string, logo: string | null): Promise<PrivateNetwork>;
  reset(caip2: string): Promise<PrivateNetwork>;
  remove(caip2: string): Promise<void>;
}

/**
 * A row the page can show, or `null` for one it cannot parse.
 *
 * Dropped rather than thrown: one malformed row must not blank the networks the
 * caller does have, and the backend serves the view model this schema is
 * generated from, so a miss here is drift the schema test catches first.
 */
export function networkFromWire(raw: unknown): PrivateNetwork | null {
  const parsed = PrivateNetworkViewSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/** The POST body: only the fields the caller set (the request model is strict). */
export function createBody(input: CreateNetworkInput): Record<string, unknown> {
  const body: Record<string, unknown> = {
    name: input.name.trim(),
    network_kind: input.networkKind,
  };
  if (input.chainId != null) body.chain_id = input.chainId;
  if (input.caip2) body.caip2 = input.caip2.trim();
  if (input.family) body.family = input.family;
  if (input.participantId) body.participant_id = input.participantId.trim();
  const token = input.nativeToken?.trim();
  if (token) body.native_token = token;
  if (input.ownerRung) body.owner_rung = input.ownerRung;
  return body;
}

function served(raw: unknown): PrivateNetwork {
  const row = networkFromWire(asRecord(raw).network);
  if (!row) throw new Error("The platform answered with a network this page cannot read.");
  return row;
}

function itemPath(caip2: string): string {
  return `${PATH}/${encodeURIComponent(caip2)}`;
}

export function createNetworksSource(client: ApiClient): NetworksSource {
  return {
    async list(): Promise<PrivateNetwork[]> {
      // Uncached: the list changes when the caller changes it, and the push
      // status (last block, genesis) is the reason anyone opens the page.
      const res = await client.get<unknown>(PATH, { cacheTtlMs: 0 });
      return rowsFromWire(unwrapData(res))
        .map(networkFromWire)
        .filter((row): row is PrivateNetwork => row !== null);
    },

    async create(input: CreateNetworkInput): Promise<PrivateNetwork> {
      // `unwrapData`, not a bare await: `ApiClient` RESOLVES an `{ok:false}`
      // envelope rather than throwing, so a refusal (the id is a public
      // chain's, or already yours) would otherwise read as a created network.
      return served(unwrapData(await client.post<unknown>(PATH, createBody(input))));
    },

    async setLogo(caip2: string, logo: string | null): Promise<PrivateNetwork> {
      return served(unwrapData(await client.patch<unknown>(itemPath(caip2), { logo })));
    },

    async reset(caip2: string): Promise<PrivateNetwork> {
      return served(unwrapData(await client.post<unknown>(`${itemPath(caip2)}/reset`, {})));
    },

    async remove(caip2: string): Promise<void> {
      unwrapData(await client.delete<void>(itemPath(caip2)));
    },
  };
}
